/**
 * Maps the internal wizard payload to the API request shape.
 *
 * The wizard stores form-friendly field names (interviewName, interview_type, bookedSlot, etc.).
 * The API expects different field names (name, kind, start_time, end_time, etc.).
 * This module bridges the gap so the rest of the code stays clean.
 */
import type {
  ScheduleInterviewPayload,
  ScheduleMode,
} from '../config/schedule.types';
import { normalizeTimeZoneForAtsCalendarApi } from '@/api/calendarEvents';
import { convertToFormData } from '@/lib/util';
import { scheduleTypeFor, type FindTimeValue } from '../calendar/selfSchedule';

// UI-only field names the DetailsStep carries in `payload.details`
// that the wizard doesn't need to send to the API. `bookedSlot` and
// `selfScheduleSlots` are deliberately NOT in this list — they drive
// mapDetailsToApi's start_time/end_time/time_slots emission and must
// survive into that mapper. The UI-only keys below would otherwise
// be silently dropped by the backend permit list, but we strip them
// defensively so request bodies stay small + logs readable.
const UI_ONLY_FIELDS = [
  'dateTime',
  'findTime',
  'book_meeting_room',
  'candidatePhone',
  'candidate_phone',
  'isCandidatePhoneEditing',
  'isDateTimeEditing',
] as const;

interface ContextIds {
  ats_application_id?: number;
  ats_job_id?: number;
  ats_job_stage_id?: number;
  ats_job_stage_action_id?: number | null;
  candidate_id?: number;
}

function applyContextIdsForMode(
  ids: ContextIds,
  mode: ScheduleMode
): Record<string, unknown> {
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';
  // /ats_schedules accepts `contact_id` but NOT `candidate_id`; the
  // interview endpoint is the opposite. Rename here so the caller can
  // keep a single `candidate_id` field in the wizard's context IDs.
  if (isSelfSchedule && typeof ids.candidate_id === 'number') {
    const { candidate_id, ...rest } = ids;
    return { ...rest, contact_id: candidate_id };
  }
  return { ...ids };
}

interface BookedSlot {
  start: string;
  end: string;
  duration?: number;
}

interface ScheduleSlot {
  start: string;
  end: string;
  duration?: number;
  is_custom?: boolean;
  override_type?: string;
}

function stripUiOnlyFields(
  obj: Record<string, unknown>
): Record<string, unknown> {
  const cleaned = { ...obj };
  for (const key of UI_ONLY_FIELDS) {
    delete cleaned[key];
  }
  return cleaned;
}

/**
 * Map details form values → API-compatible fields.
 */
function mapDetailsToApi(
  details: Record<string, unknown>,
  mode: ScheduleMode
): Record<string, unknown> {
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';

  const mapped: Record<string, unknown> = {};

  // Interview name: form uses `interviewName`, API uses `name`
  if ('interviewName' in details) {
    mapped.name = details.interviewName;
  }

  // Interview type field name diverges between endpoints:
  //   /ats_interviews — accepts `kind` (and also `interview_kind` for new
  //   callers); legacy call sites use `kind`.
  //   /ats_schedules — accepts ONLY `interview_kind` (see
  //   ats_schedules_helper.rb schedule_params_keys).
  // Send the right field per mode so self-schedule doesn't silently drop
  // the value.
  if ('interview_type' in details) {
    if (isSelfSchedule) {
      mapped.interview_kind = details.interview_type;
    } else {
      mapped.kind = details.interview_type;
    }
  }

  // Participant IDs: always send as number array
  if ('participant_ids' in details) {
    const raw = details.participant_ids;
    if (Array.isArray(raw)) {
      mapped.participant_ids = raw.map(Number);
    } else if (raw) {
      mapped.participant_ids = [Number(raw)];
    } else {
      mapped.participant_ids = [];
    }
  }

  // Duration — NOT in /ats_interviews permit list. Backend computes
  // it from (end_time - start_time) on the attendee record. For
  // /ats_schedules duration IS permitted, so forward it there.
  if (isSelfSchedule && 'duration' in details) {
    mapped.duration = Number(details.duration) || 30;
  }

  // Location
  if ('location' in details) {
    mapped.location = details.location;
  }

  // Host ID (Zoom only)
  if (details.host_id) {
    mapped.host_id = Number(details.host_id);
  }

  // Office ID (Onsite only)
  if (details.ats_office_id) {
    mapped.ats_office_id = Number(details.ats_office_id);
  }

  // HackerRank template — only when location is hackerrank. API accepts
  // the raw string/number id; preserve falsy nulls so an unset template
  // doesn't get coerced.
  if (details.location === 'hackerrank' && details.ext_interview_template_id) {
    mapped.ext_interview_template_id = details.ext_interview_template_id;
  }

  // Calendar visibility: form stores boolean, API expects string
  if ('calendar_visibility' in details) {
    mapped.calendar_visibility = details.calendar_visibility
      ? 'private'
      : 'default';
  }

  // Note taker
  if ('note_taker_enabled' in details) {
    mapped.note_taker_enabled = Boolean(details.note_taker_enabled);
  }

  // Fill with AI
  if ('fill_with_ai_enabled' in details) {
    mapped.fill_with_ai_enabled = Boolean(details.fill_with_ai_enabled);
  }

  // Calendar info — only when plan allows shared calendars (flag synced in DetailsStep)
  const sharedCalEnabled = Boolean(details.shared_calendar_enabled);
  const rawCal = details.calendar_info as
    | { provider_calendar_id?: string }
    | null
    | undefined;
  const hasCalendarInfo =
    sharedCalEnabled &&
    rawCal &&
    typeof rawCal === 'object' &&
    typeof rawCal.provider_calendar_id === 'string' &&
    rawCal.provider_calendar_id.length > 0;

  if (hasCalendarInfo) {
    mapped.calendar_info = details.calendar_info;
  }

  // Meeting rooms: map email id(s) to meeting_rooms_attributes format.
  // Single-select ships a bare string, multi-select ships string[] —
  // normalise both to string[] before emitting so the backend sees the
  // room when the user picked exactly one.
  if ('meeting_room_ids' in details) {
    const raw = details.meeting_room_ids;
    const emails: string[] = Array.isArray(raw)
      ? raw.filter((v): v is string => typeof v === 'string' && v.length > 0)
      : typeof raw === 'string' && raw.length > 0
        ? [raw]
        : [];
    if (emails.length > 0) {
      mapped.meeting_rooms_attributes = emails.map((email) => ({
        email,
        name: email,
      }));
    }
  }

  // Timezone from shared state. The backend validates against
  // ActiveSupport::TimeZone::MAPPING.invert (see config/initializers/tz.rb
  // + ats_interviews_controller.rb#set_time_zone), which accepts only
  // canonical IANA names. Legacy aliases (`Asia/Calcutta`, `US/Pacific`…)
  // return `err_invalid_timezone`. Run the same normaliser we use for
  // calendar-events + interviewer-schedule requests.
  if ('timezone' in details) {
    const raw = typeof details.timezone === 'string' ? details.timezone : '';
    mapped.timezone = raw ? normalizeTimeZoneForAtsCalendarApi(raw) : raw;
  }

  // Manual scheduling: extract start_time/end_time from bookedSlot.
  // Both are REQUIRED by AtsInterview model validations
  // (`err_start_time_missing`, `err_end_time_missing`) and a nil pair
  // also cascades into `err_duration_less_or_eq_to_zero` on the
  // attendee (duration = (end - start)/60 = 0). DetailsStep.validate()
  // should catch this first; if we see the warning below something
  // else skipped validation.
  if (!isSelfSchedule) {
    const bookedSlot = details.bookedSlot as BookedSlot | null | undefined;
    if (bookedSlot?.start && bookedSlot?.end) {
      mapped.start_time = bookedSlot.start;
      mapped.end_time = bookedSlot.end;
    }
  }

  // Self-schedule: extract time_slots from selfScheduleSlots and send
  // the `schedule_type` the backend permit-list requires. The allowed
  // values match the legacy `nextWeekMap`:
  //   'Custom'          — any user-chosen / manually-picked slots
  //   'Next weekday'    — Find Time: next weekday
  //   'Next N weekdays' — Find Time: 1–5 weekdays
  //   'Next week'       — Find Time: next full week
  //   'Next 2 weeks'    — Find Time: next two weeks
  // `findTimeValue` comes in from DetailsStep's local state; the mapper
  // converts it back to the legacy string via `scheduleTypeFor`.
  // `null` (user has not picked a preset or has manually edited after
  // applying) maps to 'Custom', matching legacy fallback behaviour.
  if (isSelfSchedule) {
    const slots = details.selfScheduleSlots as ScheduleSlot[] | undefined;
    if (Array.isArray(slots) && slots.length > 0) {
      mapped.time_slots = slots.map((slot) => ({
        start_at: slot.start,
        end_at: slot.end,
        is_custom: slot.is_custom ?? false,
        ...(slot.override_type ? { override_type: slot.override_type } : {}),
      }));
    }
    const findTimeValue = (details.findTimeValue ?? null) as FindTimeValue;
    mapped.schedule_type = scheduleTypeFor(findTimeValue);
    mapped.schedule_slots_modified = true;
    mapped.schedule_type_modified = true;
  }

  return mapped;
}

/**
 * Build the final API payload from the wizard's internal payload shape.
 */
/**
 * Map the ScorecardStep's internal shape to what the backend permit
 * list actually accepts:
 *   - scorecard_required (boolean, top-level)
 *   - scorecard_config: { is_feedback_mandatory, questions[] }
 *   - scorecard_feedback_description, scorecard_rating_description
 * Ignore UI-only keys like `enabled`, `ai_enabled`, `template_id`.
 */
interface ScorecardShape {
  enabled?: boolean;
  ai_enabled?: boolean;
  template_id?: string;
  is_feedback_mandatory?: boolean;
  questions?: Array<{
    id?: string;
    question?: string;
    type?: string;
    description?: string;
    mandatory?: boolean;
    options?: string[];
  }>;
  feedbackDescription?: string;
  ratingDescription?: string;
}

function mapScorecardToApi(
  scorecard: Record<string, unknown>
): Record<string, unknown> {
  const s = scorecard as ScorecardShape;
  const out: Record<string, unknown> = {};

  if (typeof s.enabled === 'boolean') {
    out.scorecard_required = s.enabled;
  }

  // `fill_with_ai_enabled` is a single backend column covering BOTH
  // AI-scorecard-fill AND AI-note-taker. The Details step's note-taker
  // switch already emits it via `mapDetailsToApi`; the Scorecard step
  // also exposes an "Allow AI to complete scorecard" toggle and its
  // value needs to reach the payload when the user changes it there.
  // Spread order in `buildSchedulePayload` makes this mapping win over
  // the Details mapping when both are present — last-edit wins.
  if (typeof s.ai_enabled === 'boolean') {
    out.fill_with_ai_enabled = s.ai_enabled;
  }

  const hasQuestions = Array.isArray(s.questions) && s.questions.length > 0;
  if (hasQuestions || typeof s.is_feedback_mandatory === 'boolean') {
    out.scorecard_config = {
      is_feedback_mandatory: Boolean(s.is_feedback_mandatory),
      questions: (s.questions ?? []).map((q) => ({
        type: q.type ?? '',
        question: q.question ?? '',
        ...(q.description ? { description: q.description } : {}),
        ...(typeof q.mandatory === 'boolean' ? { mandatory: q.mandatory } : {}),
        ...(Array.isArray(q.options) && q.options.length > 0
          ? { options: q.options }
          : {}),
      })),
    };
  }

  if (typeof s.feedbackDescription === 'string' && s.feedbackDescription) {
    out.scorecard_feedback_description = s.feedbackDescription;
  }
  if (typeof s.ratingDescription === 'string' && s.ratingDescription) {
    out.scorecard_rating_description = s.ratingDescription;
  }

  return out;
}

/**
 * Map the CommunicationStep's reducer-shape payload into the exact
 * keys `/ats_interviews` accepts on the CORE-4040 backend.
 *
 * Accepted (calendar invite):
 *   - candidate_template_id       → calendar-invite template to candidate
 *   - candidate_description       → calendar-invite body HTML override
 *   - interviewer_template_id     → calendar-invite template to interviewer
 *   - interviewer_description     → calendar-invite body HTML override
 *
 * Accepted (send toggles, CORE-4040):
 *   - send_calendar_invite_to_candidate / _interviewer
 *   - send_email_to_candidate / _interviewer
 *   - send_ics_to_candidate / _interviewer
 *
 * Accepted (nested email configs, CORE-4040 — require multipart):
 *   - candidate_email_comm_config:
 *       { from, name, subject, body, cc[], bcc[], attachments[] }
 *   - interviewer_email_comm_config: (same shape)
 *
 * Attachments can be binary files — the caller must wrap the returned
 * object in `convertToFormData(...)` so the Rails permit list at
 * `ats_interviews_controller.rb` receives multipart blobs where it
 * expects them.
 */
interface CommTemplateBlock {
  template?: { value?: string | number } | null;
  subject?: string;
  body?: string;
}
interface EmailCommConfig {
  subject?: string;
  body?: string;
  attachments?: unknown[];
  comm_template_id?: number;
  from_user_id?: number;
  cc_user_ids?: number[];
  bcc_user_ids?: number[];
}
interface CommPayloadShape {
  calendar_invite_candidate?: CommTemplateBlock;
  calendar_invite_interviewer?: CommTemplateBlock;
  candidate_email_comm_config?: EmailCommConfig;
  interviewer_email_comm_config?: EmailCommConfig;
  send_calendar_invite_to_candidate?: boolean;
  send_calendar_invite_to_interviewer?: boolean;
  send_email_to_candidate?: boolean;
  send_email_to_interviewer?: boolean;
  send_ics_to_candidate?: boolean;
  send_ics_to_interviewer?: boolean;
  _ui_calendar_candidate_name?: string;
  _ui_calendar_interviewer_name?: string;
}

function templateIdFrom(
  block: CommTemplateBlock | undefined
): number | undefined {
  const raw = block?.template?.value;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function descriptionFrom(block: CommTemplateBlock | undefined): string {
  return typeof block?.body === 'string' ? block.body : '';
}

/**
 * Shape our EmailCommConfig into the nested attributes the Rails
 * permit list expects: `{from, name, subject, body, cc[], bcc[],
 * attachments[]}`. Users come through as ids on `from_user_id` /
 * `cc_user_ids` / `bcc_user_ids`; the Rails side expects them under
 * `from`, `cc`, `bcc`.
 */
function mapEmailCommConfigToNested(
  cfg: EmailCommConfig | undefined,
  templateName: string | undefined
): Record<string, unknown> | undefined {
  if (!cfg) return undefined;
  const out: Record<string, unknown> = {};
  if (typeof cfg.from_user_id === 'number') out.from = cfg.from_user_id;
  if (typeof templateName === 'string' && templateName) out.name = templateName;
  if (typeof cfg.subject === 'string') out.subject = cfg.subject;
  if (typeof cfg.body === 'string') out.body = cfg.body;
  if (Array.isArray(cfg.cc_user_ids) && cfg.cc_user_ids.length > 0) {
    out.cc = cfg.cc_user_ids;
  }
  if (Array.isArray(cfg.bcc_user_ids) && cfg.bcc_user_ids.length > 0) {
    out.bcc = cfg.bcc_user_ids;
  }
  if (Array.isArray(cfg.attachments) && cfg.attachments.length > 0) {
    out.attachments = cfg.attachments;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function mapCommunicationToApi(
  communication: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!communication) return {};
  const comm = communication as unknown as CommPayloadShape;
  const out: Record<string, unknown> = {};

  // Calendar-invite template + body override (legacy contract).
  const candTemplateId = templateIdFrom(comm.calendar_invite_candidate);
  if (candTemplateId !== undefined) {
    out.candidate_template_id = candTemplateId;
  }
  const candDesc = descriptionFrom(comm.calendar_invite_candidate);
  if (candDesc) out.candidate_description = candDesc;

  const intTemplateId = templateIdFrom(comm.calendar_invite_interviewer);
  if (intTemplateId !== undefined) {
    out.interviewer_template_id = intTemplateId;
  }
  const intDesc = descriptionFrom(comm.calendar_invite_interviewer);
  if (intDesc) out.interviewer_description = intDesc;

  // Send toggles (CORE-4040). Only emit when the state has a concrete
  // boolean — an undefined toggle means "let the backend use its
  // default", so don't write `false` over a server-side `true`.
  if (typeof comm.send_calendar_invite_to_candidate === 'boolean') {
    out.send_calendar_invite_to_candidate =
      comm.send_calendar_invite_to_candidate;
  }
  if (typeof comm.send_calendar_invite_to_interviewer === 'boolean') {
    out.send_calendar_invite_to_interviewer =
      comm.send_calendar_invite_to_interviewer;
  }
  if (typeof comm.send_email_to_candidate === 'boolean') {
    out.send_email_to_candidate = comm.send_email_to_candidate;
  }
  if (typeof comm.send_email_to_interviewer === 'boolean') {
    out.send_email_to_interviewer = comm.send_email_to_interviewer;
  }
  if (typeof comm.send_ics_to_candidate === 'boolean') {
    out.send_ics_to_candidate = comm.send_ics_to_candidate;
  }
  if (typeof comm.send_ics_to_interviewer === 'boolean') {
    out.send_ics_to_interviewer = comm.send_ics_to_interviewer;
  }

  // Nested email comm configs. Attachments may be raw File blobs —
  // the caller must serialise the whole payload with
  // `convertToFormData` so multipart is produced.
  const candEmail = mapEmailCommConfigToNested(
    comm.candidate_email_comm_config,
    comm._ui_calendar_candidate_name
  );
  if (candEmail) out.candidate_email_comm_config = candEmail;
  const intEmail = mapEmailCommConfigToNested(
    comm.interviewer_email_comm_config,
    comm._ui_calendar_interviewer_name
  );
  if (intEmail) out.interviewer_email_comm_config = intEmail;

  return out;
}

/**
 * Map the Slot Selection Email step's stored shape (EmailCommConfig)
 * to the `candidate_mail_*` keys the `/ats_schedules` permit list
 * accepts. The step stores values under generic names (`subject`,
 * `body`, `from`, `cc`, `bcc`), but the backend's schedule endpoint
 * does NOT permit those at the root — they must be renamed.
 *
 * See `project_interview_manager_backend_audit` memory for the full
 * `ats_schedules_helper.rb` permit list.
 */
function mapSlotSelectionEmailToApi(
  slotSelectionEmail: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!slotSelectionEmail || typeof slotSelectionEmail !== 'object') return {};
  const src = slotSelectionEmail as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  if (typeof src.subject === 'string' && src.subject.length > 0) {
    out.candidate_mail_subject = src.subject;
  }
  if (typeof src.body === 'string' && src.body.length > 0) {
    out.candidate_mail_body = src.body;
  }
  if (src.from_organizer === true) {
    out.candidate_mail_from = null;
  } else if (typeof src.from === 'number' && Number.isFinite(src.from)) {
    out.candidate_mail_from = src.from;
  }
  if (Array.isArray(src.cc) && src.cc.length > 0) {
    out.candidate_mail_cc = src.cc;
  }
  if (Array.isArray(src.bcc) && src.bcc.length > 0) {
    out.candidate_mail_bcc = src.bcc;
  }
  if (Array.isArray(src.attachments) && src.attachments.length > 0) {
    out.candidate_mail_attachments = src.attachments;
  }
  if (src._ui_template_id !== null && src._ui_template_id !== undefined) {
    out.candidate_mail_template_id = src._ui_template_id;
  }
  // Flag that the mail was touched this save — mirrors legacy
  // `candidate_mail_updated: hasEmailDataChanged()`. We default to
  // `true` because the wizard emits this only after the user has
  // stepped through the screen at least once.
  out.candidate_mail_updated = true;
  return out;
}

export function buildSchedulePayload(
  payload: ScheduleInterviewPayload,
  contextIds: ContextIds,
  mode: ScheduleMode
): Record<string, unknown> {
  const details = stripUiOnlyFields(payload.details);
  const mappedDetails = mapDetailsToApi(details, mode);
  const mappedContextIds = applyContextIdsForMode(contextIds, mode);
  const mappedScorecard = mapScorecardToApi(payload.scorecard);
  const mappedCommunication = mapCommunicationToApi(payload.communication);
  const isSelfSchedule =
    mode === 'self-schedule' || mode === 'self-schedule-adhoc';
  const mappedSlotSelectionEmail = isSelfSchedule
    ? mapSlotSelectionEmailToApi(payload.slotSelectionEmail)
    : {};
  // Manual/adhoc modes don't have a slot selection email — but the
  // hydrated `communication.candidate_email_comm_config` is a
  // legitimate audience-email there, so it stays. Self-schedule
  // splits into two panes (slot selection email + calendar invite),
  // so `candidate_email_comm_config` would duplicate the slot-
  // selection email on the wire — strip it.
  const mappedCommunicationForMode = isSelfSchedule
    ? (() => {
        const { candidate_email_comm_config: _drop, ...rest } =
          mappedCommunication;
        return rest;
      })()
    : mappedCommunication;

  return {
    ...mappedContextIds,
    ...mappedDetails,
    ...mappedCommunicationForMode,
    ...mappedScorecard,
    ...mappedSlotSelectionEmail,
  };
}

/**
 * Same fields as the JSON `buildSchedulePayload`, serialised as
 * `FormData` so per-audience email configs can carry raw `File` blobs
 * under `attachments[]`. Mirrors InterviewActivity's
 * `buildInterviewActivityApiFormData`. Use for /ats_interviews POST +
 * /ats_schedules POST so attachment uploads survive the Rails permit
 * list.
 */
export function buildSchedulePayloadFormData(
  payload: ScheduleInterviewPayload,
  contextIds: ContextIds,
  mode: ScheduleMode
): FormData {
  const jsonPayload = buildSchedulePayload(payload, contextIds, mode);
  const formData = convertToFormData(jsonPayload);

  // `convertToFormData` drops empty arrays. The interview permit list
  // expects `participant_ids` to be present even when the roster is
  // empty — preserve legacy behaviour from InterviewActivity.
  const participantIds = jsonPayload.participant_ids;
  if (Array.isArray(participantIds) && participantIds.length === 0) {
    formData.append('participant_ids[]', '');
  }

  return formData;
}
